import { useState, useEffect } from 'react';
import Modal from './Modal';
import Button from './Button';
import LoadingSpinner from './LoadingSpinner';
import { sendMessage } from '../../services/api';

function MessageModal({ 
    isOpen, 
    onClose, 
    recipientId, 
    recipientName,
    onMessageSent = null 
}) {
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [successMessage, setSuccessMessage] = useState('');

    // Reset form when modal opens/closes
    useEffect(() => {
        if (isOpen) {
            setSubject('');
            setMessage('');
            setErrors({});
            setSuccessMessage('');
        }
    }, [isOpen]);

    const validateForm = () => {
        const newErrors = {};

        if (!subject.trim()) {
            newErrors.subject = 'Subject is required';
        } else if (subject.length > 100) {
            newErrors.subject = 'Subject must be less than 100 characters';
        }

        if (!message.trim()) {
            newErrors.message = 'Message is required';
        } else if (message.length > 5000) {
            newErrors.message = 'Message must be less than 5000 characters';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        setIsLoading(true);
        setErrors({});

        try {
            await sendMessage({
                recipientId,
                subject: subject.trim(),
                message: message.trim()
            });

            setSuccessMessage('Message sent successfully!');
            
            // Call callback if provided
            if (onMessageSent) {
                onMessageSent();
            }

            // Close modal after short delay
            setTimeout(() => {
                onClose();
            }, 1500);

        } catch (error) {
            setErrors({
                submit: error.message || 'Failed to send message. Please try again.'
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        if (isLoading) return;
        onClose();
    };

    if (!isOpen) return null;

    return (
        <Modal 
            onClose={handleCancel} 
            title={`Send Message to ${recipientName}`}
            className="message-modal"
        >
            {successMessage ? (
                <div className="message-success">
                    <div className="success-icon">✓</div>
                    <p>{successMessage}</p>
                </div>
            ) : (
                <form onSubmit={handleSubmit} className="message-form">
                    <div className="form-group">
                        <label htmlFor="message-subject" className="form-label">
                            Subject *
                        </label>
                        <input
                            id="message-subject"
                            type="text"
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            className={`form-input ${errors.subject ? 'error' : ''}`}
                            placeholder="Enter message subject"
                            maxLength={100}
                            disabled={isLoading}
                        />
                        {errors.subject && (
                            <span className="form-error">{errors.subject}</span>
                        )}
                        <div className="char-count">
                            {subject.length}/100
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="message-content" className="form-label">
                            Message *
                        </label>
                        <textarea
                            id="message-content"
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            className={`form-textarea ${errors.message ? 'error' : ''}`}
                            placeholder={`Write your message to ${recipientName}...`}
                            rows={6}
                            maxLength={5000}
                            disabled={isLoading}
                        />
                        {errors.message && (
                            <span className="form-error">{errors.message}</span>
                        )}
                        <div className="char-count">
                            {message.length}/5000
                        </div>
                    </div>

                    {errors.submit && (
                        <div className="form-error submit-error">
                            {errors.submit}
                        </div>
                    )}

                    <div className="form-actions">
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={handleCancel}
                            disabled={isLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="primary"
                            disabled={isLoading || !subject.trim() || !message.trim()}
                        >
                            {isLoading ? (
                                <>
                                    <LoadingSpinner size="small" />
                                    Sending...
                                </>
                            ) : (
                                'Send Message'
                            )}
                        </Button>
                    </div>
                </form>
            )}
        </Modal>
    );
}

export default MessageModal;
